import { useState, useEffect, useCallback } from "react";
import { GetAllCommentsService } from "shared/services/comment";
import { handleErrors } from "shared/utils/helper";
import { Message } from "shared/types/chatBoard";
import { mapCommentToMessage, mergeAndSortMessages } from "shared/utils/chatBoardUtils";

interface UseChatBoardCommentsProps {
  chatBoardId?: string | number;
  propMessages?: Message[];
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
}

export default function useChatBoardComments({
  chatBoardId,
  propMessages = [],
  messagesContainerRef,
}: UseChatBoardCommentsProps) {
  const [messages, setMessages] = useState<Message[]>(propMessages);
  const [loading, setLoading] = useState(false);
  const [commentsPage, setCommentsPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);

  const fetchComments = useCallback(async (page: number = 1, append: boolean = false) => {
    if (!chatBoardId || commentsLoading) return;

    try {
      setCommentsLoading(true);
      const limit = 10;
      const response = await GetAllCommentsService(chatBoardId, page, limit);

      if (response.status === 200) {
        let apiComments: any[] = [];

        if (Array.isArray(response.data)) {
          apiComments = response.data;
        } else if (Array.isArray(response.data?.data)) {
          apiComments = response.data.data;
        } else if (Array.isArray(response.data?.comments)) {
          apiComments = response.data.comments;
        } else if (response.data?.data && typeof response.data.data === "object") {
          const dataObj = response.data.data;
          if (Array.isArray(dataObj)) {
            apiComments = dataObj;
          } else {
            const arrayValues = Object.values(dataObj).filter((item) => Array.isArray(item)) as any[][];
            apiComments = (arrayValues[0] as any[]) || [];
          }
        }

        const mappedMessages = apiComments?.map((comment) => mapCommentToMessage(comment));

        const total = response.data?.total || response.data?.count || response.data?.data?.total || response.data?.data?.count;

        if (append) {
          const container = messagesContainerRef.current;
          const previousScrollHeight = container?.scrollHeight || 0;
          const previousScrollTop = container?.scrollTop || 0;

          setMessages((prev) => {
            const newMessages = mergeAndSortMessages([...mappedMessages, ...prev]);
            const hasMore = mappedMessages.length === limit && (total === undefined || newMessages.length < total);
            setHasMoreComments(hasMore);

            requestAnimationFrame(() => {
              if (container) {
                const newScrollHeight = container.scrollHeight;
                const heightDifference = newScrollHeight - previousScrollHeight;
                container.scrollTop = previousScrollTop + heightDifference;
              }
            });

            return newMessages;
          });
        } else {
          const reversedMessages = mappedMessages ? [...mappedMessages].reverse() : [];
          setMessages(mergeAndSortMessages(reversedMessages));
          const hasMore = mappedMessages.length === limit && (total === undefined || reversedMessages.length < total);
          setHasMoreComments(hasMore);

          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          }, 100);
        }

        setCommentsPage(page);
      }
    } catch (error: any) {
      handleErrors(error);
      if (!append) {
        setMessages([]);
        setHasMoreComments(false);
      }
    } finally {
      setCommentsLoading(false);
      setLoading(false);
    }
  }, [chatBoardId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMoreComments = useCallback(() => {
    if (hasMoreComments && !commentsLoading && chatBoardId) {
      fetchComments(commentsPage + 1, true);
    }
  }, [hasMoreComments, commentsLoading, chatBoardId, commentsPage, fetchComments]);

  useEffect(() => {
    if (chatBoardId) {
      setCommentsPage(1);
      setMessages([]);
      setLoading(true);
      fetchComments(1, false);
    } else if (propMessages.length > 0) {
      setMessages(propMessages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatBoardId]);

  return {
    messages,
    setMessages,
    loading,
    commentsLoading,
    hasMoreComments,
    loadMoreComments,
    fetchComments,
  };
}

